import { useEffect } from 'react'
import { ArticleBody } from '@/components/ArticleBody'
import { DocsLayout } from '@/components/DocsLayout'
import { Feedback } from '@/components/Feedback'
import { PlanBadge } from '@/components/PlanBadge'
import { TableOfContents } from '@/components/TableOfContents'
import { trackEvent } from '@/lib/api'
import { formatType, getCategoryLabel } from '@/lib/categories'
import { SITE_NAME, SITE_URL } from '@/lib/config'
import { useArticle } from '@/lib/data'
import {
  addHeadingIds,
  estimateReadMinutes,
  extractHeadings,
  formatDate,
} from '@/lib/format'
import { useHead } from '@/lib/head'
import { rewriteArticleHtml, toAppUrl } from '@/lib/links'
import { ArrowSquareOutIcon, CaretRightIcon } from '@phosphor-icons/react'
import { Link, useParams } from 'react-router-dom'

import NotFoundPage from './NotFound'

export default function ArticlePage() {
  const { id = '' } = useParams()
  const state = useArticle(id)
  const article = state.status === 'ready' ? state.article : null
  const path = `/articles/${encodeURIComponent(id)}`

  useHead({
    title: article ? `${article.title} — ${SITE_NAME}` : SITE_NAME,
    description: article?.summary,
    path,
    noIndex: state.status === 'missing' || state.status === 'error',
    jsonLd: article
      ? {
          '@context': 'https://schema.org',
          '@type': 'TechArticle',
          headline: article.title,
          description: article.summary,
          datePublished: article.publishedAt,
          dateModified: article.updatedAt,
          url: `${SITE_URL}${path}`,
          publisher: { '@type': 'Organization', name: 'Scrambl' },
          articleSection: getCategoryLabel(article.moduleSlug),
        }
      : undefined,
  })

  useEffect(() => {
    if (!article) return
    trackEvent({
      eventType: 'help_article_viewed',
      helpId: article.id,
      moduleSlug: article.moduleSlug,
    })
    if (window.location.hash) {
      document
        .getElementById(window.location.hash.slice(1))
        ?.scrollIntoView({ block: 'start' })
    } else {
      window.scrollTo({ top: 0 })
    }
  }, [article?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (state.status === 'missing') return <NotFoundPage />

  if (state.status === 'error') {
    return (
      <DocsLayout>
        <div className="page">
          <div className="notice notice-error">
            We couldn't load this article. Refresh to try again.
          </div>
        </div>
      </DocsLayout>
    )
  }

  if (!article) {
    return (
      <DocsLayout>
        <div className="page article-skeleton">
          <span style={{ width: '30%' }} />
          <span className="title" style={{ width: '70%' }} />
          <span style={{ width: '95%' }} />
          <span style={{ width: '85%' }} />
          <span className="block" />
        </div>
      </DocsLayout>
    )
  }

  const html = rewriteArticleHtml(addHeadingIds(article.body))
  const headings = extractHeadings(html)
  const categoryLabel = getCategoryLabel(article.moduleSlug)

  return (
    <DocsLayout
      activeCategorySlug={article.moduleSlug}
      activeArticleId={article.id}
      rail={<TableOfContents items={headings} />}
    >
      <article className="page article">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Help Center</Link>
          {article.moduleSlug ? (
            <>
              <CaretRightIcon size={11} />
              <Link
                to={`/categories/${encodeURIComponent(article.moduleSlug)}`}
              >
                {categoryLabel}
              </Link>
            </>
          ) : null}
          <CaretRightIcon size={11} />
          <span>{formatType(article.type)}</span>
        </nav>

        <h1 className="article-title">{article.title}</h1>
        {article.summary ? (
          <p className="article-summary">{article.summary}</p>
        ) : null}
        <div className="article-meta">
          <PlanBadge plans={article.featurePlans} />
          <span>{estimateReadMinutes(article.body)} min read</span>
          <span>Updated {formatDate(article.updatedAt)}</span>
        </div>

        <ArticleBody html={html} />

        {article.appPaths.length > 0 ? (
          <section className="article-aside">
            <h2>Where to find this in Scrambl</h2>
            <div className="app-paths">
              {article.appPaths.map(appPath => {
                const isExternal = /^https?:\/\//i.test(appPath)
                return (
                  <a
                    key={appPath}
                    href={isExternal ? appPath : toAppUrl(appPath)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-path"
                  >
                    <code>{appPath}</code>
                    <ArrowSquareOutIcon size={13} />
                  </a>
                )
              })}
            </div>
          </section>
        ) : null}

        {article.related.length > 0 ? (
          <section className="article-aside">
            <h2>Related articles</h2>
            <ul className="related-list">
              {article.related.map(related => (
                <li key={related.id}>
                  <Link to={`/articles/${encodeURIComponent(related.id)}`}>
                    {related.title}
                  </Link>
                  {related.moduleSlug ? (
                    <span className="related-category">
                      {getCategoryLabel(related.moduleSlug)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <Feedback helpId={article.id} />
      </article>
    </DocsLayout>
  )
}
